<template>
  <!-- Auto-synced group: owned by the Sync tab, read-only here -->
  <ConditionGroupCard v-if="isReadOnly" v-bind="$attrs" :label="groupLabel">
    <template #meta><span :class="syncTagClass">Auto synced</span></template>
    <div class="flex items-center gap-2.5 py-2 text-[15px] leading-[1.4] text-ink">
      <ArrowPathRoundedSquareIcon class="size-[18px] shrink-0 text-muted" aria-hidden="true" />
      <span v-if="syncConditionType === 'tags'">
        If tagged <strong>{{ syncTagLabel }}</strong>
      </span>
      <span v-else>
        If the field <strong>{{ customField }}</strong> equals "{{ props.condition.and?.[0]?.value }}"
      </span>
    </div>
  </ConditionGroupCard>

  <!-- One group: its rules joined by the group's own operator -->
  <ConditionGroupCard
    v-else-if="inner"
    v-bind="$attrs"
    :label="groupLabel"
    removable
    :add-operator="conditionType"
    @remove="emits('remove:group')"
    @add="addCondition">
    <template #meta>
      <Select
        v-if="widgetTargeting"
        size="small"
        :modelValue="widgetGroupType"
        @update:modelValue="updateWidgetGroupType"
        optionLabel="label"
        optionValue="value"
        :options="widgetGroupTypeOptions" />
      <span v-else-if="conditionGroups.length > 1" class="font-semibold" :class="operatorTextClass[conditionType]"
        >{{ conditionType === 'and' ? 'All' : 'Any' }} of these</span
      >
    </template>
    <template v-for="(item, index) in conditionGroups" :key="index">
      <span v-if="index > 0" :class="[ruleChipClass, operatorChipClass[conditionType]]">{{
        conditionType.toUpperCase()
      }}</span>
      <ConditionGroup
        v-if="isGroupNode(item)"
        inner
        group-label="Nested group"
        :condition="item"
        :widget-targeting="widgetTargeting"
        :usage-context="usageContext"
        :source-fields="sourceFields"
        :form-schema="formSchema"
        @remove:group="removeCondition(index)"
        @condition:updated="emits('condition:updated', $event)" />
      <Condition
        v-else
        :condition="item"
        :widget-targeting="widgetTargeting"
        :source-fields="sourceFields"
        :form-schema="formSchema"
        @update:condition="updateCondition(index, $event)"
        @delete:condition="removeCondition(index)" />
    </template>
  </ConditionGroupCard>

  <!-- Top level: groups joined by the Any/All operator -->
  <div v-else v-bind="$attrs" class="flex flex-col gap-5">
    <!-- Only shown where the operator is editable; answer rules always match any group -->
    <div
      v-if="widgetTargeting ? changeType || condition.not : canChangeTopOperator && conditionGroups.length > 0"
      class="flex flex-wrap items-center gap-3">
      <span class="text-sm leading-6 font-bold text-primary-900 dark:text-heading">A visitor matches when</span>
      <Select
        v-if="widgetTargeting"
        :modelValue="widgetGroupType"
        @update:modelValue="updateWidgetGroupType"
        optionLabel="label"
        optionValue="value"
        :options="widgetGroupTypeOptions" />
      <SelectButton
        v-else
        :modelValue="conditionType"
        :options="topOperatorOptions"
        optionLabel="label"
        optionValue="value"
        :allowEmpty="false"
        aria-label="A visitor matches when"
        @update:modelValue="setTopOperator" />
    </div>

    <div
      v-if="conditionGroups.length === 0 && hasUserInteracted"
      class="rounded-md bg-stone-200/30 p-4 text-center text-stone-600 dark:bg-slate-900/90 dark:text-white/70">
      No conditions added yet
    </div>
    <div v-else-if="conditionGroups.length > 0" class="flex flex-col">
      <template v-for="(group, index) in conditionGroups" :key="index">
        <div v-if="index > 0" class="flex flex-col items-center" data-test="group-connector">
          <div class="h-3 w-0.5 opacity-35" :class="operatorLineClass[conditionType]" />
          <button
            type="button"
            class="rounded-full border border-current px-3.5 py-1.5 text-xs leading-none font-bold tracking-[.12em] transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary enabled:cursor-pointer"
            :class="operatorChipClass[conditionType]"
            :disabled="!canChangeTopOperator"
            :title="canChangeTopOperator ? 'Switch operator' : undefined"
            @click="setTopOperator(oppositeOperator(conditionType))">
            {{ conditionType.toUpperCase() }}
          </button>
          <div class="h-3 w-0.5 opacity-35" :class="operatorLineClass[conditionType]" />
        </div>

        <div v-if="shouldDeferInnerGroups && isGroupNode(group)" class="flex flex-col gap-2">
          <div class="flex items-center gap-2 text-[13px] font-bold text-heading">
            <span>Group {{ index + 1 }}</span>
            <Button
              size="small"
              severity="secondary"
              text
              :label="deferredGroupSummaryLabel(group)"
              :aria-expanded="expandedDeferredGroupIndex === index"
              :aria-controls="deferredGroupId(index)"
              @click="toggleDeferredGroup(index)" />
          </div>
          <div v-if="expandedDeferredGroupIndex === index" :id="deferredGroupId(index)">
            <ConditionGroup
              inner
              :group-label="`Group ${index + 1}`"
              :condition="group"
              :widget-targeting="widgetTargeting"
              :usage-context="usageContext"
              :source-fields="sourceFields"
              :form-schema="formSchema"
              @remove:group="removeGroup(index)"
              @condition:updated="emits('condition:updated', $event)" />
          </div>
        </div>
        <ConditionGroup
          v-else-if="isGroupNode(group)"
          inner
          :group-label="`Group ${index + 1}`"
          :condition="group"
          :widget-targeting="widgetTargeting"
          :usage-context="usageContext"
          :source-fields="sourceFields"
          :form-schema="formSchema"
          @remove:group="removeGroup(index)"
          @condition:updated="emits('condition:updated', $event)" />
        <!-- A rule stored directly under the top level is a one-rule group -->
        <ConditionGroupCard
          v-else
          :label="`Group ${index + 1}`"
          removable
          :add-operator="oppositeOperator(conditionType)"
          @remove="removeCondition(index)"
          @add="expandRuleIntoGroup(index)">
          <Condition
            :condition="group"
            :widget-targeting="widgetTargeting"
            :source-fields="sourceFields"
            :form-schema="formSchema"
            @update:condition="updateCondition(index, $event)"
            @delete:condition="removeCondition(index)" />
        </ConditionGroupCard>
      </template>
    </div>

    <!-- AI inline input: shows below auto-sync badge when no user-defined conditions.
         mt-4 keeps its protruding Righty badge inside scroll containers such as drawers. -->
    <RightyInline
      v-if="userDefinedConditionCount === 0 && !hasUserInteracted"
      ref="aiInlineRef"
      class="mt-4"
      taskType="generate_conditions"
      placeholder="People who visited /pricing in the last week but are not on my email list"
      buttonLabel="Generate Conditions"
      :context="aiContext"
      :showResponse="false"
      minHeight="5rem"
      @result="handleAiResult" />

    <div class="flex flex-wrap items-center justify-between gap-3 pt-1.5">
      <div class="flex flex-wrap items-center gap-3">
        <Button label="Add condition group" size="small" severity="secondary" outlined @click="addGroup">
          <template #icon><PlusIcon class="size-4" aria-hidden="true" /></template>
        </Button>
        <RightyButton
          v-if="userDefinedConditionCount > 0"
          label="AI Generate"
          size="small"
          @click="showAiModal = true" />
      </div>
      <slot name="actions" />
    </div>
  </div>

  <Dialog
    v-if="!inner"
    :visible="showAiModal"
    modal
    :closable="true"
    :style="{ width: '50rem' }"
    @update:visible="showAiModal = $event"
    appendTo="body">
    <template #header>
      <h3 class="text-lg font-semibold"> Generate Conditions with AI </h3>
    </template>
    <RightyInline
      ref="aiModalRef"
      class="mt-4"
      taskType="generate_conditions"
      placeholder="Add a condition for visitors from the US, or remove the pageview condition"
      buttonLabel="Generate Conditions"
      :context="aiContextWithExisting"
      :showResponse="false"
      @result="handleAiResult" />
  </Dialog>
</template>

<script setup>
import { computed, ref } from 'vue'
import { ArrowPathRoundedSquareIcon, PlusIcon } from '@heroicons/vue/24/outline'
import RightyInline from '@dashboard/modules/righty/components/RightyInline.vue'
import RightyButton from '@dashboard/components/common/RightyButton.vue'
import { flushPendingDebouncedFields } from '@dashboard/composables/useDebouncedField'
import { useToast } from 'primevue/usetoast'
import { validateLogicDefinition, autoFixCondition } from '@dashboard/modules/righty/services/conditionValidator'
import { countLogicDefinitionConditions } from '@dashboard/modules/segment/services/segmentUtilities'
import {
  operatorChipClass,
  operatorTextClass,
  oppositeOperator
} from '@dashboard/modules/segment/services/conditionSummary'
import { getIntegrationData } from '@dashboard/modules/integration/services/integrationDataUtilities'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import Condition from '@dashboard/modules/segment/components/Condition.vue'
import ConditionGroupCard from '@dashboard/modules/segment/components/ConditionGroupCard.vue'
import { useQuestionStore } from '@dashboard/modules/segment/stores/questionStore'
import ConditionGroup from '@dashboard/modules/segment/components/ConditionGroup.vue'
import { computedAsync } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Select from 'primevue/select'
import SelectButton from 'primevue/selectbutton'
import { inject, watch } from 'vue'
defineOptions({ inheritAttrs: false })

const toast = useToast()

const props = defineProps({
  condition: Object,
  widgetTargeting: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  inner: { type: Boolean, default: false },
  // Whether the top-level Any/All operator can change. Answer rules (Auto-Segment) stay Any:
  // sync injects its group into the top-level `or`.
  changeType: { type: Boolean, default: true },
  groupLabel: { type: String, default: 'Group' },
  deferInnerGroups: { type: Boolean, default: false },
  deferredGroupIdPrefix: { type: String, default: '' },
  sourceFields: { type: Array, default: null },
  formSchema: { type: Array, default: null },
  usageContext: { type: String, default: 'segment signal auto-segmentation' }
})

const integrationStore = useIntegrationStore()
const questionStore = useQuestionStore()
const { integratedPartner } = storeToRefs(integrationStore)

const emits = defineEmits(['remove:group', 'condition:updated'])
const segmentGroup = inject('segmentGroup', null)

const conditionGroup = computed(() =>
  props.widgetTargeting ? (props.condition.not ?? props.condition) : props.condition
)
const conditionType = computed(() => Object.keys(conditionGroup.value)[0])

const widgetGroupType = computed(() => {
  const group = props.condition.not ?? props.condition
  return `${props.condition.not ? 'not-' : ''}${group.or ? 'or' : 'and'}`
})

const updateWidgetGroupType = (value) => {
  const children = conditionGroups.value
  const type = value.endsWith('or') ? 'or' : 'and'
  delete props.condition.and
  delete props.condition.or
  delete props.condition.not
  Object.assign(props.condition, value.startsWith('not-') ? { not: { [type]: children } } : { [type]: children })
}
const widgetGroupTypeOptions = [
  { label: 'At least one of the following is true', value: 'or' },
  { label: 'All of the following are true', value: 'and' },
  { label: 'None of the following are true', value: 'not-or' },
  { label: 'Not all of the following are true', value: 'not-and' }
]
const topOperatorOptions = [
  { label: 'Any group matches', value: 'or' },
  { label: 'All groups match', value: 'and' }
]
const operatorLineClass = { and: 'bg-logic-and', or: 'bg-logic-or' }
const ruleChipClass = 'ml-3.5 self-start rounded px-2 py-1 text-[11px] leading-none font-bold tracking-[.1em]'
const syncTagClass =
  'rounded-md bg-primary-soft px-2 py-1 text-xs leading-none font-bold whitespace-nowrap text-primary'

const conditionGroups = computed(() => {
  if (props.widgetTargeting) {
    const group = props.condition.not ?? props.condition
    return group.and ?? group.or ?? []
  }
  return conditionGroup.value[conditionType.value]
})

// Widget targeting may nest negated groups; segment and flow logic never contain `not`.
const isGroupNode = (node) => Boolean(node.and || node.or || (props.widgetTargeting && node.not))

const canChangeTopOperator = computed(() => props.changeType && !props.widgetTargeting)

// Flipping the top level inverts every group, so groups always use the opposite operator
// (OR of AND groups ↔ AND of OR groups). Answer rules, the only callers with
// auto-synced groups, keep a fixed top level.
const setTopOperator = (value) => {
  if (!canChangeTopOperator.value || value === conditionType.value) {
    return
  }
  const innerOperator = oppositeOperator(value)
  for (const group of conditionGroups.value) {
    const operator = group.and ? 'and' : group.or ? 'or' : null
    if (operator && operator !== innerOperator) {
      group[innerOperator] = group[operator]
      delete group[operator]
    }
  }
  updateConditionType(value)
}

// "Add condition" on a rule stored directly under the top level turns it into a group.
const expandRuleIntoGroup = (index) => {
  hasUserInteracted.value = true
  const groups = conditionGroup.value[conditionType.value]
  const innerOperator = oppositeOperator(conditionType.value)
  groups[index] = { [innerOperator]: [groups[index], {}] }
  emits('condition:updated', { action: 'add', conditionType: innerOperator, index: 1 })
}

const shouldDeferInnerGroups = computed(() => props.deferInnerGroups && !props.inner)
const expandedDeferredGroupIndex = ref(null)

const deferredGroupId = (index) => `${props.deferredGroupIdPrefix}-condition-group-${index}`

const deferredGroupSummaryLabel = (group) => {
  const conditionCount = countLogicDefinitionConditions(group)

  if (conditionCount === 0) {
    return 'Empty condition group'
  }

  const prefix = group.and ? 'All' : 'Any'
  const suffix = conditionCount === 1 ? 'condition' : 'conditions'
  return `${prefix} of ${conditionCount} ${suffix}`
}

const setExpandedDeferredGroupIndex = (index) => {
  if (expandedDeferredGroupIndex.value !== null && expandedDeferredGroupIndex.value !== index) {
    flushPendingDebouncedFields()
  }

  expandedDeferredGroupIndex.value = index
}

const toggleDeferredGroup = (index) => {
  if (!shouldDeferInnerGroups.value) {
    return
  }

  setExpandedDeferredGroupIndex(expandedDeferredGroupIndex.value === index ? null : index)
}

// Sync-managed conditions are identified by the explicit `$managedBy: 'sync'`
// marker on the condition object. The marker is applied on sync setup/repair
// by repairSegmentGroup() and is the canonical ownership signal — shape-based
// matching (same $type + customFieldId) incorrectly flagged user-authored
// rules that happened to share those fields (ENG-1009).
const isSyncManaged = (condition) => condition?.and?.length === 1 && condition?.and?.[0]?.$managedBy === 'sync'

// Index of the auto-sync condition in the outer condition groups (only at top
// level). Uses the $managedBy marker so it's mode-agnostic — previously this
// was gated on syncToCustomField, which broke tag-mode sync (no $managedBy
// detection → userDefinedConditionCount double-counted the sync condition).
const autoSyncIndex = computed(() => {
  if (props.inner) {
    return -1
  }
  const or = props.condition?.or
  if (!Array.isArray(or)) {
    return -1
  }
  return or.findIndex((group) => isSyncManaged(group))
})

// Count of user-defined conditions (excludes auto-sync)
const userDefinedConditionCount = computed(() => {
  const total = conditionGroups.value?.length || 0
  return autoSyncIndex.value >= 0 ? total - 1 : total
})

// True when sync is actively configured for this segment group. Covers both
// modes: field-mode requires syncToCustomField; tag-mode requires at least
// one segment tag mapping.
const hasActiveSync = computed(() => {
  const sg = segmentGroup?.value
  if (!sg) {
    return false
  }
  if (sg.syncMode === 'tag') {
    return Object.keys(sg.syncMappings || {}).length > 0
  }
  return !!sg.syncToCustomField
})

const isReadOnly = computed(() => isSyncManaged(props.condition))

// TODO: Refactor
const customField = computedAsync(
  async () => {
    const currentSegmentGroup = segmentGroup?.value
    if (!currentSegmentGroup || !isSyncManaged(props.condition)) {
      return '...'
    }

    if (integratedPartner?.value?.espFeatureSupport?.customField?.unstructured) {
      return currentSegmentGroup.syncToCustomField
    }

    const customFields = (await getIntegrationData('partner', 'custom_fields')).value ?? []

    return customFields.find((field) => String(field.id) === currentSegmentGroup.syncToCustomField)?.label
  },
  '...',
  { lazy: true }
)

// The sync condition's $type — drives how the Auto synced card is rendered
// (field-mode vs tag-mode wording + label source).
const syncConditionType = computed(() => props.condition?.and?.[0]?.$type)

// Resolve the human-readable label for a tag-mode sync condition by looking
// up the mapped tagId in the integration's tag data. Falls back to the raw
// tag ID if the tag is unknown (deleted in ESP but still referenced locally).
const syncTagLabel = computedAsync(async () => {
  const tagIds = props.condition?.and?.[0]?.tagIds
  if (!Array.isArray(tagIds) || tagIds.length === 0) {
    return '...'
  }
  const tagId = String(tagIds[0])
  const tags = (await getIntegrationData('partner', 'tags')).value
  return tags.find((t) => String(t.id) === tagId)?.label || tagId
}, '...')

// New groups use the opposite of the top-level operator. Widget targeting keeps AND
// groups; its group operators are chosen per group.
const addGroup = () => {
  hasUserInteracted.value = true
  const innerOperator = props.widgetTargeting ? 'and' : oppositeOperator(conditionType.value)
  conditionGroup.value[conditionType.value].push({ [innerOperator]: [{}] })

  if (shouldDeferInnerGroups.value) {
    setExpandedDeferredGroupIndex(conditionGroup.value[conditionType.value].length - 1)
  }
}

const removeGroup = (index) => {
  if (shouldDeferInnerGroups.value && expandedDeferredGroupIndex.value !== null) {
    flushPendingDebouncedFields()
  }

  const nextExpandedDeferredGroupIndex =
    expandedDeferredGroupIndex.value === null || expandedDeferredGroupIndex.value < index
      ? expandedDeferredGroupIndex.value
      : expandedDeferredGroupIndex.value === index
        ? null
        : expandedDeferredGroupIndex.value - 1

  conditionGroup.value[conditionType.value].splice(index, 1)
  expandedDeferredGroupIndex.value = nextExpandedDeferredGroupIndex

  // Reset latch when user explicitly removes all user-defined conditions,
  // so the AI inline input reappears. Safe because config-refetch race
  // conditions don't call removeGroup — they reactively replace the object.
  if (userDefinedConditionCount.value === 0) {
    hasUserInteracted.value = false
  }
}

const addCondition = () => {
  hasUserInteracted.value = true
  conditionGroup.value[conditionType.value].push({})

  // Emit condition update event for new condition
  emits('condition:updated', {
    action: 'add',
    conditionType: conditionType.value,
    index: conditionGroup.value[conditionType.value].length - 1
  })
}

const removeCondition = (index) => {
  const removedCondition = conditionGroup.value[conditionType.value][index]
  conditionGroup.value[conditionType.value].splice(index, 1)

  // Emit condition update event for removed condition
  emits('condition:updated', {
    action: 'remove',
    removedCondition,
    conditionType: conditionType.value,
    index
  })

  if (conditionGroup.value[conditionType.value].length === 0) {
    emits('remove:group')
  }
}

const updateConditionType = (value) => {
  const oldValue = conditionType.value
  const newCondition = {}
  newCondition[value] = conditionGroups.value
  delete props.condition[oldValue]
  props.condition[value] = newCondition[value]

  // Emit condition type change event
  emits('condition:updated', {
    action: 'typeChange',
    oldType: oldValue,
    newType: value,
    conditions: conditionGroups.value
  })
}

const updateCondition = (index, newCondition) => {
  const conditions = conditionGroup.value[conditionType.value]
  const oldCondition = conditions[index]

  // Only assign if it's actually a different object reference
  // This prevents unnecessary Vue reactivity triggers on every keystroke
  // (the emitted newCondition is typically the same object as conditions[index])
  if (oldCondition !== newCondition) {
    conditions[index] = newCondition
  }

  // Emit condition update event with details
  emits('condition:updated', {
    oldCondition,
    newCondition,
    index,
    conditionType: conditionType.value
  })
}

// Track if user has interacted with conditions in this component instance.
// Prevents the AI inline input from reappearing after a config save/refetch
// race condition resets conditionGroups.length to 0 momentarily.
// Also true if user-defined conditions already exist on mount (excludes auto-sync).
const hasUserInteracted = ref(userDefinedConditionCount.value > 0)

// On cold page load, integratedPartner may not be available yet, causing
// autoSyncIndex to return -1 and hasUserInteracted to latch true prematurely.
// Watch for integration data to load and correct the latch if needed. Uses
// hasActiveSync so it fires for both field-mode (syncToCustomField set) and
// tag-mode (syncMappings populated) sync configurations.
if (!props.inner && hasActiveSync.value && autoSyncIndex.value === -1) {
  const unwatch = watch(autoSyncIndex, (newIndex) => {
    if (newIndex >= 0 && userDefinedConditionCount.value === 0) {
      hasUserInteracted.value = false
    }
    unwatch()
  })
}

watch(
  () => conditionGroups.value.length,
  (length) => {
    if (expandedDeferredGroupIndex.value !== null && expandedDeferredGroupIndex.value >= length) {
      expandedDeferredGroupIndex.value = null
    }
  }
)

// AI Generate state
const showAiModal = ref(false)
const aiInlineRef = ref(null)
const aiModalRef = ref(null)

const aiContext = computed(() => ({
  usageContext: props.usageContext
}))

const aiContextWithExisting = computed(() => ({
  existingConditions: props.condition,
  usageContext: props.usageContext
}))

const fixDefinition = (node) => {
  if (Array.isArray(node?.and)) {
    return { ...node, and: node.and.map(fixDefinition) }
  }
  if (Array.isArray(node?.or)) {
    return { ...node, or: node.or.map(fixDefinition) }
  }
  if (node?.not) {
    return { ...node, not: fixDefinition(node.not) }
  }
  return node?.$source ? autoFixCondition(node, { strictPageviewPaths: true }) : node
}

const containsNot = (node) =>
  Boolean(node?.not) || [...(node?.and ?? []), ...(node?.or ?? [])].some((child) => containsNot(child))

// Negated groups exist only in widget targeting, whose root may itself be `{ not: group }`.
const applyConditionDefinition = (definition) => {
  const group = props.widgetTargeting ? (definition?.not ?? definition) : definition
  if (!Array.isArray(group?.and ?? group?.or) || (!props.widgetTargeting && containsNot(definition))) {
    return false
  }

  const fixed = fixDefinition(definition)

  const errors = validateLogicDefinition(fixed, { strictPageviewPaths: true, questions: questionStore.questions })
  if (errors.length > 0) {
    console.warn('Condition validation warnings:', errors)
    // Continue anyway - validation is advisory, conditions may reference dynamic IDs
  }

  delete props.condition.and
  delete props.condition.or
  delete props.condition.not
  Object.assign(props.condition, fixed)

  return true
}

const handleAiResult = (result) => {
  if (!result.success) {
    toast.add({
      severity: 'error',
      summary: 'Generation Failed',
      detail: result.error || 'Failed to generate conditions',
      life: 5000
    })
    return
  }

  const definition = result.data
  if (!definition) {
    toast.add({
      severity: 'error',
      summary: 'Generation Failed',
      detail: 'No conditions were generated',
      life: 5000
    })
    return
  }

  hasUserInteracted.value = true
  const applied = applyConditionDefinition(definition)
  if (!applied) {
    toast.add({
      severity: 'error',
      summary: 'Invalid Format',
      detail: 'The generated conditions had an unexpected format',
      life: 5000
    })
    return
  }

  // Capture the user's instruction before clearing
  const instruction = aiModalRef.value?.instructions || aiInlineRef.value?.instructions || ''

  // Close modal and clear inputs
  showAiModal.value = false
  aiModalRef.value?.clearInput()
  aiInlineRef.value?.clearInput()

  toast.add({
    severity: 'success',
    summary: 'Conditions Generated',
    detail: 'AI has generated the targeting conditions',
    life: 3000
  })

  const groups = definition[Object.keys(definition)[0]] || []
  const totalConditions = countLogicDefinitionConditions(definition)
  const isComplex = totalConditions >= 3 || groups.length >= 2

  emits('condition:updated', {
    action: 'ai_generated',
    instruction,
    isComplex
  })
}
</script>
